import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

/**
 * PROMPT 16-AUDIT-LOG-VIEWER (2026-08-26)
 * Tab generik untuk lihat audit_logs — semua category (project / access_control /
 * asset / user / dst.). Dipakai di UserManagementPage tab `audit_log_all`.
 * Backend endpoint: GET /api/audit-logs (server-side pagination + filter).
 * Access gated by `personnel_manage.view`.
 */
const CATEGORIES = [
  { value: "", label: "Semua kategori" },
  { value: "project", label: "Project (create/update/delete/archive)" },
  { value: "access_control", label: "Access Control Matrix" },
  { value: "user", label: "User (login/logout/edit)" },
  { value: "petty_cash", label: "Petty Cash" },
];
const ACTIONS = [
  { value: "", label: "Semua aksi" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "archive", label: "Archive" },
  { value: "restore", label: "Restore" },
  { value: "access_control_updated", label: "Access matrix updated" },
];
const PAGE_SIZES = [25, 50, 100];

export default function AuditLogViewer() {
  const [category, setCategory] = useState("");
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null); // entry _id → expanded

  // NOTE: `q` intentionally excluded from load's deps so typing in search box
  // does NOT fire API on every keystroke. `q` is applied when other filters
  // change (category/action/pageSize) or via page reset (line below).
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (category) params.category = category;
      if (action) params.action = action;
      if (q.trim()) params.q = q.trim();
      const { data: d } = await api.get("/audit-logs", { params });
      setData(d);
    } catch { setData({ items: [], total: 0 }); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, action, page, pageSize]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [category, action, q, pageSize]);
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <section className="bg-white border border-slate-200" data-testid="audit-log-viewer">
      <div className="p-3 border-b border-slate-200 grid grid-cols-1 md:grid-cols-5 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Kategori</div>
          <select data-testid="audit-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-8 border border-slate-300 bg-white px-2 text-xs rounded-none">
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Aksi</div>
          <select data-testid="audit-filter-action" value={action} onChange={(e) => setAction(e.target.value)} className="w-full h-8 border border-slate-300 bg-white px-2 text-xs rounded-none">
            {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Pencarian bebas</div>
          <div className="flex gap-1">
            <Input data-testid="audit-filter-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nama actor / project / field…" onKeyDown={(e) => e.key === "Enter" && load()} className="rounded-none h-8 text-xs" />
            <button type="button" data-testid="audit-filter-apply" onClick={load} className="text-[10px] uppercase tracking-wider font-semibold text-white bg-[#0B1D3A] hover:bg-slate-800 px-3">Cari</button>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Per halaman</div>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="w-full h-8 border border-slate-300 bg-white px-2 text-xs rounded-none" data-testid="audit-page-size">
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="audit-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-slate-600">Waktu</th>
              <th className="text-left px-3 py-2 font-semibold text-slate-600">Actor</th>
              <th className="text-left px-3 py-2 font-semibold text-slate-600">Aksi</th>
              <th className="text-left px-3 py-2 font-semibold text-slate-600">Kategori</th>
              <th className="text-left px-3 py-2 font-semibold text-slate-600">Target</th>
              <th className="text-left px-3 py-2 font-semibold text-slate-600">Field</th>
              <th className="text-right px-3 py-2 font-semibold text-slate-600 w-16">Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400 italic">Memuat…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400 italic">Tidak ada entry ditemukan</td></tr>}
            {!loading && data.items.map((row, i) => {
              const rid = row.id || `${i}`;
              const target = row.target_label || row.target_user_name || row.project_name || row.target_id || "—";
              const isOpen = detailOpen === rid;
              return (
                <>
                  <tr key={rid} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${rid}`}>
                    <td className="px-3 py-1.5 tabular-nums text-slate-600 whitespace-nowrap">{(row.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-3 py-1.5"><div className="font-semibold text-[#0B1D3A] truncate max-w-[180px]" title={row.actor_name}>{row.actor_name || "—"}</div><div className="text-[10px] text-slate-500">{row.actor_division} Lv{row.actor_level}</div></td>
                    <td className="px-3 py-1.5"><span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold ${row.action === "delete" ? "bg-rose-100 text-rose-800" : row.action === "archive" ? "bg-amber-100 text-amber-800" : row.action === "create" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{row.action}</span></td>
                    <td className="px-3 py-1.5 text-slate-600">{row.category || "—"}</td>
                    <td className="px-3 py-1.5"><div className="truncate max-w-[220px]" title={target}>{target}</div>{row.target_type && <div className="text-[10px] text-slate-500">{row.target_type}</div>}</td>
                    <td className="px-3 py-1.5 text-slate-600 font-mono text-[10px]">{row.field || "—"}</td>
                    <td className="px-3 py-1.5 text-right"><button type="button" onClick={() => setDetailOpen(isOpen ? null : rid)} data-testid={`audit-detail-${rid}`} className="text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A] hover:underline">{isOpen ? "Tutup" : "Lihat"}</button></td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <td colSpan={7} className="px-3 py-2">
                        <pre className="text-[10px] text-slate-700 whitespace-pre-wrap break-all font-mono max-h-64 overflow-auto" data-testid={`audit-detail-body-${rid}`}>{JSON.stringify({
                          old_value: row.old_value,
                          new_value: row.new_value,
                          changes: row.changes,
                          delete_reason: row.delete_reason,
                          note: row.note,
                        }, null, 2)}</pre>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Total: <span className="font-semibold text-[#0B1D3A]" data-testid="audit-total">{data.total}</span> entry</div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="audit-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-slate-800 border border-slate-300 px-2 py-1 disabled:opacity-30">◀ Prev</button>
          <span className="text-[10px] text-slate-600" data-testid="audit-page-info">Halaman {page} / {totalPages}</span>
          <button type="button" data-testid="audit-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-slate-800 border border-slate-300 px-2 py-1 disabled:opacity-30">Next ▶</button>
        </div>
      </div>
    </section>
  );
}
